import type { ReactNode } from 'react'
import type { FaqItem } from '@/types'

import { useEffect, useId, useRef, useState } from 'react'
import { Plus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { gsap, registerMotion, ScrollTrigger, prefersReducedMotion } from '@/lib/motion'

interface AccordionRowProps {
    item: FaqItem
    isOpen: boolean
    onToggle: () => void
}

const AccordionRow = ({ item, isOpen, onToggle }: AccordionRowProps): ReactNode => {
    const panel = useRef<HTMLDivElement | null>(null)
    const icon = useRef<HTMLSpanElement | null>(null)
    const id = useId()
    const mounted = useRef(false)

    useEffect(() => {
        const element = panel.current
        const mark = icon.current

        if (!element) return

        registerMotion()

        const reduced = prefersReducedMotion()
        const duration = reduced ? 0 : 0.55
        const instant = !mounted.current

        mounted.current = true

        const tween = gsap.to(element, {
            height: isOpen ? 'auto' : 0,
            opacity: isOpen ? 1 : 0,
            duration: instant ? 0 : duration,
            ease: 'nuvea',
            overwrite: true,
            onComplete: () => ScrollTrigger.refresh()
        })

        if (mark) {
            gsap.to(mark, {
                rotate: isOpen ? 135 : 0,
                duration: instant ? 0 : duration,
                ease: 'nuvea',
                overwrite: true
            })
        }

        return () => {
            tween.kill()
        }
    }, [isOpen])

    return (
        <div className='border-b border-stone-line/80' data-accordion-row=''>
            <h3>
                <button
                    type='button'
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    aria-controls={`${id}-panel`}
                    id={`${id}-trigger`}
                    className='group flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left transition-opacity duration-300 hover:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink md:py-6'
                >
                    <span className='type-body text-ink'>
                        {item.question}
                    </span>

                    <span
                        ref={icon}
                        aria-hidden='true'
                        className='flex size-7 shrink-0 items-center justify-center rounded-full border border-stone-line text-ink transition-colors duration-300 group-hover:border-ink'
                    >
                        <Plus className='size-3.5' strokeWidth={1.6} />
                    </span>
                </button>
            </h3>

            <div
                ref={panel}
                id={`${id}-panel`}
                role='region'
                aria-labelledby={`${id}-trigger`}
                className='h-0 overflow-hidden opacity-0'
            >
                <p className='type-copy max-w-[62ch] pb-6 text-slate-soft md:pb-8'>
                    {item.answer}
                </p>
            </div>
        </div>
    )
}

interface AccordionListProps {
    items: FaqItem[]
    className?: string
    /** Index that starts open. Use -1 for all closed. */
    defaultOpen?: number
}

export const AccordionList = ({ items, className, defaultOpen = -1 }: AccordionListProps): ReactNode => {
    const [open, setOpen] = useState<number>(defaultOpen)

    return (
        <div className={cn('border-t border-stone-line/80', className)}>
            {items.map((item, index) => (
                <AccordionRow
                    key={item.question}
                    item={item}
                    isOpen={open === index}
                    onToggle={() => setOpen(open === index ? -1 : index)}
                />
            ))}
        </div>
    )
}
